import { useVirtualizer } from '@tanstack/react-virtual';
import { useCallback } from 'react';
import { useSwimlaneInternalEventHandler } from '../api';
import { CardVirtualizationContext, useSwimlaneRootProps, useSwimlaneState } from '../context';
import type { IHeader } from '../types';

import { SwimlaneGrid } from './body/SwimlaneGrid';
import { CARD_WIDTH, COMPACT_CARD_WIDTH, GRID_GAP, GROUP_ROW_HEADER_HEIGHT } from './constants';
import { CardsDNDContextProvider } from './dnd';
import { AllItemsFiltered } from './empty-state/AllItemsFiltered';
import { NoItems } from './empty-state/NoItems';
import { NoSubItems } from './empty-state/NoSubItems';
import { ColumnHeaders } from './headers/columns/ColumnHeaders';
import { RowHeaders } from './headers/rows/RowHeaders';

interface ILayoutRoot {
  scrollContainer: HTMLDivElement;
}

interface IFlag {
  [key: string]: boolean;
}

function generateCompositeKey(headers: IHeader[], index: number, flags: IFlag): string {
  const flagsString = Object.keys(flags).join('-');
  return `${headers[index]?.id}${flagsString}` || index.toString();
}

export function VirtualizerRoot({ scrollContainer }: ILayoutRoot) {
  const {
    rowHeaders,
    columnHeaders,
    isCompact,
    isItemDateVisible,
    isItemDescriptionVisible,
    isItemDetailsVisible,
    emptyState,
  } = useSwimlaneRootProps();
  const { getRowHeight } = useSwimlaneState();

  const getRowItemKey = useCallback(
    (index: number) =>
      generateCompositeKey(rowHeaders, index, {
        isCompact,
        isItemDateVisible,
        isItemDescriptionVisible,
        isItemDetailsVisible,
      }),
    [rowHeaders, isCompact, isItemDateVisible, isItemDescriptionVisible, isItemDetailsVisible],
  );

  const getColumnItemKey = useCallback(
    (index: number) =>
      generateCompositeKey(columnHeaders, index, {
        isCompact,
        isItemDateVisible,
        isItemDescriptionVisible,
        isItemDetailsVisible,
      }),
    [columnHeaders, isCompact, isItemDateVisible, isItemDescriptionVisible, isItemDetailsVisible],
  );

  const columnVirtualizer = useVirtualizer({
    count: columnHeaders.length,
    estimateSize: () => (isCompact ? COMPACT_CARD_WIDTH.value : CARD_WIDTH.value),
    gap: GRID_GAP.value,
    getScrollElement: () => scrollContainer,
    horizontal: true,
    overscan: 1,
    getItemKey: getColumnItemKey,
  });

  const rowVirtualizer = useVirtualizer({
    count: Math.max(rowHeaders.length, 1),
    estimateSize: (index: number) => {
      const header = rowHeaders[index];
      if (header && 'isGroupHeader' in header && header.isGroupHeader === true) {
        return GROUP_ROW_HEADER_HEIGHT.value;
      }
      const rowID = header?.id ?? 'null';
      return getRowHeight(rowID);
    },
    measureElement: (element, _entry, instance) => {
      const index = instance.indexFromElement(element);
      const header = rowHeaders[index];
      if (header && 'isGroupHeader' in header && header.isGroupHeader === true) {
        return GROUP_ROW_HEADER_HEIGHT.value;
      }
      return getRowHeight(header?.id ?? 'null');
    },
    gap: GRID_GAP.value,
    getScrollElement: () => scrollContainer,
    getItemKey: getRowItemKey,
    overscan: 1,
  });

  const onRowHeightChange = useCallback(() => {
    rowHeaders.forEach((header, index) => {
      if ('isGroupHeader' in header && header.isGroupHeader === true) {
        rowVirtualizer.resizeItem(index, GROUP_ROW_HEADER_HEIGHT.value);
      } else {
        rowVirtualizer.resizeItem(index, getRowHeight(header.id));
      }
    });
  }, [getRowHeight, rowHeaders, rowVirtualizer]);
  useSwimlaneInternalEventHandler('swimlaneRowHeightChange', onRowHeightChange);

  return (
    <>
      <ColumnHeaders columnVirtualizer={columnVirtualizer} />
      <RowHeaders rowVirtualizer={rowVirtualizer} />
      <CardsDNDContextProvider onDragEnd={rowVirtualizer.measure}>
        {emptyState === 'no-items' && <NoItems />}
        {emptyState === 'filtered' && <AllItemsFiltered />}
        {emptyState === 'no-sub-items' && <NoSubItems />}
        {emptyState === 'no-sub-items-portfolio' && <NoSubItems isMaster />}
        {!emptyState && (
          <CardVirtualizationContext.Provider value={rowVirtualizer}>
            <SwimlaneGrid columnVirtualizer={columnVirtualizer} rowVirtualizer={rowVirtualizer} />
          </CardVirtualizationContext.Provider>
        )}
      </CardsDNDContextProvider>
    </>
  );
}
